/**
 * Client-side storage, namespaced per edition.
 *
 * Hard rule 6. The pilot uses bare keys — `ozora2026_favs`, `ozora2026_lang`,
 * `ozora2026_pins` — which is fine for one festival on one origin and
 * catastrophic the moment two editions share one. OZORA 2026 and 2027 would
 * silently overwrite each other's saved plans.
 *
 * Every key goes through here. Nothing calls `localStorage` directly.
 */
const PREFIX = 'myrave';

/**
 * A key with no edition belongs to the PLATFORM — the menu's open state, the
 * card/table choice, the language before a rave is open — and says so:
 * `myrave:platform:<name>`. It used to be `myrave:<name>`, which is a key
 * with an edition-shaped hole in it; nothing could tell the two kinds apart.
 */
const PLATFORM = 'platform';

function key(editionId: string | null, name: string): string {
  return `${PREFIX}:${editionId ?? PLATFORM}:${name}`;
}

/** Where a platform key lived before 2026-09-28. Read once, then moved. */
const legacyKey = (name: string) => `${PREFIX}:${name}`;

/**
 * Storage can throw, not just return null: Safari's private mode, a browser
 * with site data blocked, a page in a sandboxed frame. A festival app that
 * crashes because someone browses privately is a bad festival app.
 */
function safe<T>(fn: () => T, fallback: T): T {
  try {
    return fn();
  } catch {
    return fallback;
  }
}

export function readJson<T>(editionId: string | null, name: string, fallback: T): T {
  return safe(() => {
    let raw = localStorage.getItem(key(editionId, name));
    if (raw === null && editionId === null) {
      // A setting saved under the old platform key is moved, not lost.
      raw = localStorage.getItem(legacyKey(name));
      if (raw !== null) {
        localStorage.setItem(key(null, name), raw);
        localStorage.removeItem(legacyKey(name));
      }
    }
    return raw === null ? fallback : (JSON.parse(raw) as T);
  }, fallback);
}

export function writeJson(editionId: string | null, name: string, value: unknown): void {
  safe(() => localStorage.setItem(key(editionId, name), JSON.stringify(value)), undefined);
}

/**
 * The same keys in `sessionStorage`: kept while the TAB lives — a reload
 * keeps it, closing the tab clears it (the line-up editor's undo history).
 */
export function readSessionJson<T>(editionId: string | null, name: string, fallback: T): T {
  return safe(() => {
    const raw = sessionStorage.getItem(key(editionId, name));
    return raw === null ? fallback : (JSON.parse(raw) as T);
  }, fallback);
}

export function writeSessionJson(editionId: string | null, name: string, value: unknown): void {
  safe(() => sessionStorage.setItem(key(editionId, name), JSON.stringify(value)), undefined);
}

export function remove(editionId: string | null, name: string): void {
  safe(() => {
    localStorage.removeItem(key(editionId, name));
    if (editionId === null) localStorage.removeItem(legacyKey(name));
  }, undefined);
}

/** Everything this app has stored, for a "clear my data" control. */
export function clearAll(): void {
  safe(() => {
    for (const k of Object.keys(localStorage)) {
      if (k.startsWith(`${PREFIX}:`)) localStorage.removeItem(k);
    }
  }, undefined);
}

/**
 * Which editions have data on this device.
 *
 * An edition id has colons of its own (`ed:ozora:2026`), so the id is
 * everything between the prefix and the LAST colon — splitting on the first
 * one returned `ed` for every edition there is. Names never contain a colon.
 */
export function storedEditionIds(): string[] {
  return safe(() => {
    const ids = new Set<string>();
    for (const k of Object.keys(localStorage)) {
      if (!k.startsWith(`${PREFIX}:`)) continue;
      const id = k.slice(PREFIX.length + 1, k.lastIndexOf(':'));
      if (id && id !== PLATFORM && k.lastIndexOf(':') > PREFIX.length) ids.add(id);
    }
    return [...ids];
  }, []);
}
